import * as Downloadable from '../../../Data/Downloadable'
import {OverviewTab} from './OverviewState'
import {OverviewAction} from './OverviewActions'
import {InquiryWithDetails} from '../../../Entities/InquiryWithDetails'
import {update} from '../../../Data/ObjectHelper'
import * as InquiryCommentResource from '../../../Request/InquiryCommentResource'
import {
    OverviewDownloadRequestIdentifier,
    OverviewFeedback,
    OverviewRequestIdentifier
} from './OverviewFeedback'
import {Result} from '../../../Data/Result'
import {decodeHttpResponse, HttpError} from '../../../Data/Http'
import * as R from 'ramda'
import {scrollDivDown} from '../../../Data/Dom'
import * as InquiryProofCopyResource from '../../../Request/InquiryProofCopyResource'
import {inquiryCommentWithUsersDecoder} from '../../../Entities/InquiryCommentWithUsers'
import {proofCopyWithDetailsDecoder} from '../../../Entities/ProofCopyWithDetails'
import {Session} from '../../../Data/Session'
import {InquiryHistoryEntryWithUsers} from '../../../Entities/InquiryHistoryEntryWithUsers'
import {FeedbackLoop} from '../../../Data/FeedbackLoop'
import * as InquiryResource from '../../../Request/InquiryResource'
import {inquiryDecoder} from '../../../Entities/Inquiry'

export function initOverview(inquiryWithDetails: InquiryWithDetails): OverviewTab {
    return {
        inquiry: inquiryWithDetails.inquiry,
        inquiryUser: inquiryWithDetails.bpsUser,
        company: inquiryWithDetails.company,
        history: inquiryWithDetails.history,
        proofCopy: inquiryWithDetails.inquiryProofCopy,
        proofCopyDownload: {type: 'Default'},
        proofCopyUploadError: null,
        historyCommentInput: '',
        finishInquiryCommentInput: '',
        finishInquiryLoading: false,
    }
}

type OverviewLoop =
    FeedbackLoop<OverviewTab, OverviewFeedback>

export function overviewReducer(
    session: Session,
    inquiryNumber: number,
    action: OverviewAction,
    state: OverviewTab
): OverviewLoop {
    switch (action.type) {
        case 'NoOp':
            return {
                state: state,
                feedback: [],
            }
        case 'ChangedHistoryCommentInput':
            return {
                state: update(
                    {historyCommentInput: action.value},
                    state
                ),
                feedback: []
            }
        case 'SubmitHistoryComment':
            return {
                state: update(
                    {historyCommentInput: ''},
                    state
                ),
                feedback: [
                    {
                        type: 'SendRequest',
                        identifier: {type: 'InquiryCreateHistoryComment'},
                        httpRequest:
                            InquiryCommentResource.createForInquiry(
                                session.manufacturer.id,
                                inquiryNumber,
                                state.historyCommentInput
                            ),
                    },
                ],
            }
        case 'SubmitHistoryCommentResponse': {
            const result =
                decodeHttpResponse(
                    inquiryCommentWithUsersDecoder(),
                    action.result
                )
            return ((): OverviewLoop => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update({
                                history: R.append<InquiryHistoryEntryWithUsers>(
                                    {type: 'Comment', comment: result.value},
                                    state.history,
                                )
                            }, state),
                            feedback: [{
                                type: 'RunEffect',
                                effect:
                                    scrollDivDown<OverviewAction>(
                                        'history-scroll-view',
                                        {type: 'NoOp'},
                                    ),
                            }],
                        }
                    case 'Err':
                        return {
                            state: state,
                            feedback: [],
                        }
                }
            })()
        }
        case 'UploadProofCopy':
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'ProofCopyUpload'},
                    httpRequest: InquiryProofCopyResource.upload(
                        session.manufacturer.id,
                        inquiryNumber,
                        action.blob,
                    ),
                }]
            }
        case 'ProofCopyUploadResponse': {
            const result =
                decodeHttpResponse(proofCopyWithDetailsDecoder(), action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    proofCopy: result.value.proofCopy,
                                    inquiry: result.value.inquiry,
                                    history:
                                        result.value.inquiryStatusChangeWithUsers !== null ?
                                            R.append<InquiryHistoryEntryWithUsers>(
                                                {
                                                    type: 'StatusChange',
                                                    statusChange:
                                                    result.value.inquiryStatusChangeWithUsers,
                                                },
                                                state.history,
                                            ) :
                                            state.history,
                                    proofCopyUploadError: null,
                                },
                                state,
                            ),
                            effects: [
                                scrollDivDown(
                                    'history-scroll-view',
                                    {type: 'NoOp'},
                                ),
                            ],
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update(
                                {proofCopyUploadError: result.error},
                                state,
                            ),
                            effects: [],
                            feedback: [],
                        }
                }
            })()
        }
        case 'DownloadProofCopy':
            if (state.proofCopy !== null) {
                return {
                    state: update(
                        {
                            proofCopyDownload: {type: 'Loading'},
                        },
                        state,
                    ),
                    feedback: [{
                        type: 'SendDownloadRequest',
                        identifier: {type: 'ProofCopyDownload'},
                        httpRequest:
                            InquiryProofCopyResource.download(
                                state.proofCopy.id,
                            ),
                        filename: state.proofCopy.filename,
                    }]
                }
            } else {
                return {state, feedback: []}
            }
        case 'ProofCopyDownloadResponse':
            return {
                state: update(
                    {
                        proofCopyDownload:
                            Downloadable.fromResult(action.result),
                    },
                    state,
                ),
                feedback: [],
            }
        case 'FinishInquiryCommentChanged':
            return {
                state: update(
                    {finishInquiryCommentInput: action.value},
                    state,
                ),
                feedback: [],
            }
        case 'FinishInquiry':
            return {
                state: update({finishInquiryLoading: true}, state),
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'InquiryFinish'},
                    httpRequest: InquiryResource.finish(
                        state.inquiry.id,
                        {comment: state.finishInquiryCommentInput},
                    ),
                }],
            }
        case 'InquiryFinishResponse': {
            const result =
                decodeHttpResponse(inquiryDecoder(), action.result)
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: update(
                                {
                                    inquiry: result.value,
                                    finishInquiryCommentInput: '',
                                    finishInquiryLoading: false,
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update({finishInquiryLoading: false}, state),
                            feedback: [],
                        }
                }
            })()
        }
    }
}

export function overviewResponseAction(
    identifier: OverviewRequestIdentifier,
    result: Result<HttpError, string>
): OverviewAction {
    switch (identifier.type) {
        case 'InquiryCreateHistoryComment':
            return {
                type: 'SubmitHistoryCommentResponse',
                result,
            }
        case 'ProofCopyUpload':
            return {
                type: 'ProofCopyUploadResponse',
                result,
            }
        case 'InquiryFinish':
            return {
                type: 'InquiryFinishResponse',
                result,
            }
    }
}

export function overviewDownloadResponseAction(
    identifier: OverviewDownloadRequestIdentifier,
    result: Result<HttpError, null>
): OverviewAction {
    switch (identifier.type) {
        case 'ProofCopyDownload':
            return {
                type: 'ProofCopyDownloadResponse',
                result,
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/InquiryDetails/Overview/OverviewReducer.ts